import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { generateSEOData } from '@/components/feature/SEOHead';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const filteredArticles = articles.filter((a) => a.categorySlug === currentCategory.slug);

  const seo = generateSEOData({
    title: `${currentCategory.name} - 黄游下戟`,
    description: currentCategory.description,
    canonicalUrl: `/category/${currentCategory.slug}`,
  });

  const siteUrl = 'https://huangyouxiaji.com';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `黄游下戟 - ${currentCategory.name}`,
        url: `${siteUrl}/category/${currentCategory.slug}`,
        description: currentCategory.description,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `${siteUrl}/category/${currentCategory.slug}` },
          ],
        },
      },
    ],
  };

  const articleImages: Record<string, string> = {
    'huangyou-xiashuo-chuchu': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20scrolls%20and%20bamboo%20slips%20with%20calligraphy%2C%20warm%20lighting%2C%20scholarly%20atmosphere%2C%20traditional%20Chinese%20study%20room%2C%20ink%20and%20brush%20on%20wooden%20desk%2C%20soft%20natural%20light%2C%20elegant%20and%20refined%20composition&width=800&height=500&seq=cat-chuchu-01&orientation=landscape',
    'huangyou-xiashuo-gushi': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20warrior%20scholar%20in%20traditional%20robes%20holding%20a%20halberd%2C%20ancient%20Chinese%20courtyard%2C%20autumn%20leaves%2C%20misty%20atmosphere%2C%20ink%20wash%20painting%20style%2C%20elegant%20and%20serene%20mood&width=800&height=500&seq=cat-gushi-01&orientation=landscape',
    'huangyou-xiashuo-xiangzheng': 'https://readdy.ai/api/search-image?query=Symbolic%20ancient%20Chinese%20halberd%20weapon%20resting%20against%20a%20stone%20wall%2C%20soft%20diffused%20lighting%2C%20minimalist%20composition%2C%20ink%20wash%20aesthetic%2C%20serene%20and%20contemplative%20atmosphere&width=800&height=500&seq=cat-xiangzheng-01&orientation=landscape',
    'huangyou-xiashuo-zhexue': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20philosophy%20scene%2C%20abstract%20ink%20wash%20landscape%20with%20misty%20mountains%2C%20contemplative%20atmosphere%2C%20Taoist%20philosophy%20concept%2C%20minimalist%20composition%2C%20serene%20and%20profound%20mood&width=800&height=500&seq=cat-zhexue-01&orientation=landscape',
    'huangyou-xiashuo-tangshi': 'https://readdy.ai/api/search-image?query=Tang%20dynasty%20poetry%20scene%2C%20ancient%20Chinese%20scholar%20writing%20calligraphy%20under%20moonlight%2C%20traditional%20Chinese%20garden%2C%20ink%20wash%20painting%20style%2C%20elegant%20and%20romantic%20atmosphere&width=800&height=500&seq=cat-tangshi-01&orientation=landscape',
    'huangyou-xiashuo-songci': 'https://readdy.ai/api/search-image?query=Song%20dynasty%20literati%20gathering%2C%20elegant%20Chinese%20garden%20with%20pavilion%2C%20scholars%20reciting%20poetry%2C%20soft%20candlelight%2C%20traditional%20Chinese%20aesthetic%2C%20refined%20and%20cultured%20atmosphere&width=800&height=500&seq=cat-songci-01&orientation=landscape',
    'huangyou-xiashuo-xiandai': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20zen%20study%20room%2C%20blend%20of%20traditional%20Chinese%20and%20contemporary%20design%2C%20warm%20natural%20lighting%2C%20peaceful%20and%20contemplative%20atmosphere%2C%20clean%20lines%2C%20serene%20composition&width=800&height=500&seq=cat-xiandai-01&orientation=landscape',
    'huangyou-xiashuo-yishu': 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20ink%20wash%20painting%20of%20a%20scholar%20in%20landscape%2C%20misty%20mountains%2C%20flowing%20water%2C%20ancient%20pine%20trees%2C%20elegant%20brushwork%2C%20classical%20Chinese%20art%20aesthetic&width=800&height=500&seq=cat-yishu-01&orientation=landscape',
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: currentCategory.name },
            ]}
          />
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600 mx-auto mb-4">
                <i className={`${currentCategory.icon} text-2xl`}></i>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-foreground-500 text-sm max-w-lg mx-auto">
                {currentCategory.description}
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      currentCategory.slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                >
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={articleImages[article.id] || ''}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 mb-3">
                      {article.category}
                    </span>
                    <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-foreground-400">
                      <span>{article.publishDate}</span>
                      <span>·</span>
                      <span>{article.readTime} 分钟阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">暂无此分类下的文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}